import React, {useState, useEffect} from 'react';
import {ScrollView, View, Image, Text, Pressable} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {useIsFocused} from '@react-navigation/native';
import SearchBox from '../Shared/SearchBox';
import Icon from '../Shared/Icon';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import CustomPressable from '../Shared/CustomPressable';
import {Navigation} from '../../Constants/Navigation';
import {STORAGE_KEYS} from '../../Constants/AppConstants';
import LoadingSpinner from '../Shared/LoadingSpinner';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import DynamicRadioControl from '../DynamicControls/DynamicRadioControl';

export declare type ListManagementProps = {
  navigation: any;
};

export type RecordType = 'basic' | 'master-detail' | 'name-value';

export type Record = {
  label: string;
  name: string;
  count: number;
  type: RecordType;
  isReadOnly: boolean;
};

const ListManagement = (props: ListManagementProps): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<Record[]>([]);
  const [selectedRecord, setSelectedRecord] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showRecordTypes, setShowRecordTypes] = useState('all');

  const [showAll, setShowAll] = useState(false);

  const {
    animalTypes,
    certificationTypes,
    classificationUnits,
    crews,
    departments,
    dispositions,
    documentTypes,
    hazardTypes,
    incidentClassifications,
    incidentProbabilities,
    incidentTypes,
    jobs,
    mechanisms,
    locations,
    locationTypes,
    OIICS_BodyParts,
    OIICS_Natures,
    OIICS_Sources,
    peopleTypes,
    severityLevels,
    shifts,
    tasks,
    taskTypes,
    trainingTypes,
    powersyncReady,
  } = usePowerSyncData();

  const isFocused = useIsFocused();

  useEffect(() => {
    fetchRecords();
  }, [powersyncReady]);

  useEffect(() => {
    if (isFocused) fetchRecords();
    else setIsLoading(false);
  }, [isFocused]);

  const fetchRecords = async () => {
    let mappedRecords: Record[] = [];

    mappedRecords.push({
      label: 'Animal Types',
      name: ListDataTypes.ANIMALTYPES,
      count: animalTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Body Parts',
      name: ListDataTypes.BODYPARTS,
      count: OIICS_BodyParts.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Certification Types',
      name: ListDataTypes.CERTIFICATIONTYPES,
      count: certificationTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Classification Units',
      name: ListDataTypes.CLASSIFICATIONUNITS,
      count: classificationUnits.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Crews',
      name: ListDataTypes.CREWS,
      count: crews.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Departments',
      name: ListDataTypes.DEPARTMENTS,
      count: departments.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Dispositions',
      name: ListDataTypes.DISPOSTIONS,
      count: dispositions.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Document Types',
      name: ListDataTypes.DOCUMENTTYPES,
      count: documentTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'HazardTypes',
      name: ListDataTypes.HAZARDTYPES,
      count: hazardTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Incident Classifications',
      name: ListDataTypes.INCIDENTCLASSIFICATIONS,
      count: incidentClassifications.length,
      type: 'master-detail',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Incident Probabilities',
      name: ListDataTypes.INCIDENTPROBABILITIES,
      count: incidentProbabilities.length,
      type: 'name-value',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Incident Types',
      name: ListDataTypes.INCIDENTTYPES,
      count: incidentTypes.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Jobs',
      name: ListDataTypes.JOBS,
      count: jobs.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Locations',
      name: ListDataTypes.LOCATIONS,
      count: locations.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'LocationTypes',
      name: ListDataTypes.LOCATIONTYPES,
      count: locationTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Mechanisms/ Events',
      name: ListDataTypes.MECHANISMS,
      count: mechanisms.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Natures',
      name: ListDataTypes.NATURES,
      count: OIICS_Natures.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'People Types',
      name: ListDataTypes.PEOPLETYPES,
      count: peopleTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Severity Levels',
      name: ListDataTypes.SEVERITYLEVELS,
      count: severityLevels.length,
      type: 'name-value',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Shifts',
      name: ListDataTypes.SHIFTS,
      count: shifts.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Sources',
      name: ListDataTypes.SOURCES,
      count: OIICS_Sources.length,
      type: 'basic',
      isReadOnly: true,
    });

    mappedRecords.push({
      label: 'Tasks',
      name: ListDataTypes.TASKS,
      count: tasks.length,
      type: 'master-detail',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Task Types',
      name: ListDataTypes.TASKTYPES,
      count: taskTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords.push({
      label: 'Training Types',
      name: ListDataTypes.TRAININGTYPES,
      count: trainingTypes.length,
      type: 'basic',
      isReadOnly: false,
    });

    mappedRecords = sortItems(mappedRecords);
    setRecords(mappedRecords);

    setIsLoading(false);
  };

  function sortItems(itemsToSort: Record[]): Record[] {
    return itemsToSort.sort((a, b) =>
      (a.label?.toLowerCase() ?? '') > (b.label?.toLowerCase() ?? '')
        ? 1
        : (a.label?.toLowerCase() ?? '') < (b.label?.toLowerCase() ?? '')
          ? -1
          : 0,
    );
  }

  function viewRecords(record: Record): void {
    props.navigation.push(Navigation.EDITLISTMANAGEMENT, {
      listName: record.name,
      listLabel: record.label,
      listType: record.type,
      isReadOnly: record.isReadOnly,
    });
  }

  const renderRecords = (): React.ReactElement => {
    let filteredRecords = records.filter(
      r =>
        // Search filter
        search === '' || r.label?.toLowerCase().includes(search.toLowerCase()),
    );
    filteredRecords = filteredRecords.filter(x =>
      showRecordTypes == 'all'
        ? true
        : showRecordTypes == 'user'
          ? x.isReadOnly === false
          : x.isReadOnly === true,
    );

    let visibleRecords = showAll
      ? filteredRecords
      : filteredRecords.slice(0, 10);
    if (visibleRecords.length === 0)
      return (
        <View style={ReportsStyleSheet.list}>
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {textAlign: 'center', color: Colors.darkestGreen},
            ]}>
            No Results Found
          </Text>
        </View>
      );

    return (
      <View style={ReportsStyleSheet.listNoTopPadding}>
        {visibleRecords.map((record, index) => {
          return renderRecord(record, index);
        })}
        {filteredRecords.length > visibleRecords.length && (
          <Pressable
            style={({pressed}) => [
              CommonStyleSheet.smallGreenButton,
              {alignSelf: 'center', marginTop: 24, marginBottom: 24},
              pressed && {
                opacity: 0.8,
              },
            ]}
            onPress={() => setShowAll(true)}>
            <Text style={CommonStyleSheet.smallGreenButtonText}>Show All</Text>
          </Pressable>
        )}
      </View>
    );
  };

  const renderRecord = (record: Record, index: number): React.ReactElement => {
    return (
      <View
        key={index}
        style={{
          flexDirection: 'row',
          borderBottomWidth: 0.4,
          borderBottomColor: Colors.darkGreen,
        }}>
        <Pressable
          style={({pressed}) => [
            ReportsStyleSheet.listItemContainer,
            {flex: 1},
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
            },
          ]}
          onPress={() => setSelectedRecord(record.name)}>
          <Text style={ReportsStyleSheet.listItemName} numberOfLines={2}>
            {record.label} ({record.count} records){' '}
            {record.isReadOnly && (
              <Text
                style={{
                  color: 'grey',
                }}>
                - System Default
              </Text>
            )}
          </Text>
        </Pressable>
        {selectedRecord && selectedRecord === record.name
          ? renderActions(record)
          : null}
      </View>
    );
  };

  const renderActions = (record: Record): React.ReactElement => {
    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}>
        <CustomPressable
          style={({pressed}) => [
            pressed && {
              opacity: 0.6,
            },
            ReportsStyleSheet.draftAction,
          ]}
          tooltip={'View Records'}
          onPress={() => viewRecords(record)}>
          <Icon icon={'eye'} color={Colors.white} size={20} />
        </CustomPressable>
      </View>
    );
  };
  const renderFilters = () => {
    return (
      <>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            marginHorizontal: 20,
            marginTop: 6,
          }}>
          <DynamicRadioControl
            config={{
              radios: [
                {label: 'Show all', value: 'all'},
                {label: 'Custom', value: 'user'},
                {label: 'System Default', value: 'system'},
              ],
            }}
            onChange={(
              controlId: number | undefined,
              controlTypeId: number | undefined,
              value: string,
              isValid: boolean,
              metaDataKey: string | null,
              metaDataValue: string | null,
            ) => {
              setShowRecordTypes(value);
            }}
            radioGroupStyling={{flexDirection: 'row'}}
            value={showRecordTypes}
          />
        </View>
      </>
    );
  };
  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <View style={{height: 260, backgroundColor: Colors.darkestGreen}}>
        <Text style={ReportsStyleSheet.headerTitle}>List Management</Text>
      </View>
      <View style={{flexDirection: 'row'}}>
        <View style={{flex: 2, padding: 28}}>
          <SearchBox onChangeSearch={newSearch => setSearch(newSearch)} />
          {renderFilters()}
          {renderRecords()}
        </View>
        <View style={{flex: 1}}>
          <View style={CommonStyleSheet.bcsfFooter}>
            <Image
              source={Images.BCSF}
              style={CommonStyleSheet.bcsfFooterImage}
            />
            <Text style={CommonStyleSheet.bcsfFooterText}>
              BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
            </Text>
          </View>
        </View>
      </View>
      <LoadingSpinner message='Loading' visible={isLoading} />
    </ScrollView>
  );
};

export default ListManagement;
